// TODO(legal-review): this text is an agent-drafted placeholder. A qualified DSGVO reviewer must
// verify or replace before public launch. Key items flagged inline with TODO(legal-review).

type LegalPage = "impressum" | "datenschutz";

export function LegalView({ page }: { page: LegalPage }) {
  return (
    <div className="min-h-screen bg-rc-cream text-rc-black font-sans antialiased selection:bg-rc-lime selection:text-rc-black">
      <LegalHeader />
      <main className="max-w-marketing mx-auto px-6 sm:px-8 py-12 sm:py-16">
        <div className="max-w-[44rem]">
          {page === "impressum" ? <Impressum /> : <Datenschutz />}
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}

function LegalHeader() {
  return (
    <header
      className="sticky top-0 z-50 border-b border-rc-fog"
      style={{
        background: "rgba(250, 248, 244, 0.86)",
        backdropFilter: "saturate(140%) blur(10px)",
        WebkitBackdropFilter: "saturate(140%) blur(10px)",
      }}
    >
      <div className="max-w-marketing mx-auto px-6 sm:px-8 py-4 flex items-center gap-4">
        <a href="/" aria-label="READYCAL" className="inline-flex items-center">
          <img src="/brand/readycal-logo.png" alt="READYCAL" className="h-6 w-auto block" />
        </a>
        <a
          href="/"
          className="ml-auto text-[13px] font-medium text-rc-charcoal hover:text-rc-berry transition-colors"
        >
          ← Zurück
        </a>
      </div>
    </header>
  );
}

function LegalFooter() {
  return (
    <footer className="border-t border-rc-fog">
      <div className="max-w-marketing mx-auto px-6 sm:px-8 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[12px] text-rc-charcoal">
        <div>© {new Date().getFullYear()} READYCAL · net positive ventures GmbH</div>
        <div className="flex gap-6">
          <a href="/datenschutz" className="hover:text-rc-berry transition-colors">Datenschutz</a>
          <a href="/impressum" className="hover:text-rc-berry transition-colors">Impressum</a>
        </div>
      </div>
    </footer>
  );
}

function H1({ children }: { children: React.ReactNode }) {
  return (
    <h1 className="font-display font-bold text-display-md tracking-display text-rc-black mb-10">
      {children}
    </h1>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-display font-semibold text-[22px] sm:text-[24px] tracking-display text-rc-black mt-10 mb-3">
      {children}
    </h2>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[16px] leading-[1.65] text-rc-charcoal mb-4">{children}</p>
  );
}

function Address({ children }: { children: React.ReactNode }) {
  return (
    <address className="not-italic text-[16px] leading-[1.65] text-rc-charcoal mb-4 whitespace-pre-line">
      {children}
    </address>
  );
}

// ── Impressum ─────────────────────────────────────────────────────────────────
// TODO(legal-review): Verify the legal entity name, address, and registration details.
// The entity below (net positive ventures GmbH) is the current operating entity.
// If READYCAL GmbH is incorporated separately, update accordingly.

function Impressum() {
  return (
    <>
      <div className="text-eyebrow font-semibold uppercase text-rc-charcoal mb-5">Rechtliches</div>
      <H1>Impressum</H1>

      <H2>Anbieter</H2>
      <P>
        REASONZ ist ein Produkt von READYCAL, betrieben durch:
      </P>

      {/* TODO(legal-review): Confirm exact legal entity, address and Registernummer before launch. */}
      <H2>Angaben gemäß § 5 TMG</H2>
      <Address>
{`net positive ventures GmbH
c/o SleevesUp!
Bödekerstraße 1
30161 Hannover`}
      </Address>

      <H2>Vertreten durch</H2>
      {/* TODO(legal-review): Confirm vertretungsberechtigte Personen. */}
      <P>Björn Waide, Geschäftsführer</P>

      <H2>Kontakt</H2>
      {/* TODO(legal-review): Confirm telephone number and switch contact email if needed. */}
      <P>
        Telefon: +49 151 40160831<br />
        E-Mail:{" "}
        <a href="mailto:info@readycal.de" className="text-rc-berry underline underline-offset-2 decoration-rc-berry/40 hover:decoration-rc-berry">
          info@readycal.de
        </a>
      </P>

      <H2>Registereintrag</H2>
      {/* TODO(legal-review): Confirm Registernummer. */}
      <P>
        Eintragung im Handelsregister.<br />
        Registergericht: Amtsgericht Hannover<br />
        Registernummer: HRB 224136
      </P>

      {/* TODO(legal-review): Add USt-IdNr once confirmed. */}
      <H2>Umsatzsteuer-Identifikationsnummer</H2>
      <P>
        Gemäß § 27a Umsatzsteuergesetz: [USt-IdNr. bitte eintragen — TODO(legal-review)]
      </P>

      <H2>Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV</H2>
      {/* TODO(legal-review): Confirm name and address of the person responsible for content. */}
      <Address>
{`Björn Waide
c/o SleevesUp!
Bödekerstraße 1
30161 Hannover`}
      </Address>
    </>
  );
}

// ── Datenschutz ───────────────────────────────────────────────────────────────
// TODO(legal-review): All sections must be reviewed by a qualified DSGVO practitioner
// before public launch. Particularly sensitive items are flagged inline.

function Datenschutz() {
  return (
    <>
      <div className="text-eyebrow font-semibold uppercase text-rc-charcoal mb-5">Rechtliches</div>
      <H1>Datenschutzerklärung</H1>

      <H2>1. Datenschutz auf einen Blick</H2>
      <P>
        REASONZ erhebt nur die Daten, die für den Betrieb des Live-Insight-Systems notwendig sind.
        Antworten von Teilnehmenden werden anonym erfasst; die Plattform fragt weder Name noch
        E-Mail-Adresse noch sonstige Identifikationsmerkmale ab.
      </P>

      {/* ── 2. Welche Daten werden erhoben ─────────────────────────────────────────────── */}
      <H2>2. Welche Daten werden erhoben?</H2>
      <P>
        Bei der Nutzung von REASONZ als Teilnehmende werden folgende Daten verarbeitet:
      </P>
      <ul className="list-disc list-inside text-[16px] leading-[1.65] text-rc-charcoal mb-4 space-y-1 pl-1">
        <li>
          <strong>Antworttext</strong> — der von Ihnen eingegebene freie Text.
        </li>
        <li>
          <strong>Stream-ID / geo_tag</strong> (optional) — falls Sie einem Stadtteil-Stream
          zugeordnet sind, wird dieser Wert zusammen mit Ihrer Antwort gespeichert.
        </li>
        <li>
          <strong>Fingerprint-Hash</strong> — zur Missbrauchsprävention (Rate-Limiting) wird ein
          technischer Hash berechnet, der keine Rückschlüsse auf Ihre Identität erlaubt. Die
          IP-Adresse selbst wird nach der Hash-Berechnung nicht gespeichert.
        </li>
      </ul>
      <P>
        Es werden <strong>keine</strong> personenbezogenen Daten wie Name, E-Mail-Adresse oder
        persistente Nutzer-IDs von Teilnehmenden erhoben.
      </P>

      {/* ── 3. Zweck der Datenverarbeitung ─────────────────────────────────────────────── */}
      <H2>3. Zweck der Verarbeitung</H2>
      <P>
        Die erhobenen Daten dienen ausschließlich der Bereitstellung von Echtzeit-Engagement-
        Insights für Events und Workshops. KI-gestützte Auswertungen (Clustering, Beobachtungen)
        werden unmittelbar während des Events erzeugt und dem Facilitator angezeigt.
      </P>

      {/* ── 4. Rechtsgrundlage ─────────────────────────────────────────────────────────── */}
      <H2>4. Rechtsgrundlage</H2>
      <P>
        Die Verarbeitung der Antworten erfolgt auf Basis Ihrer <strong>Einwilligung</strong> gemäß
        Art. 6 Abs. 1 lit. a DSGVO — Sie erteilen diese durch das aktive Absenden Ihrer Antwort.
        Der Fingerprint-Hash zur Rate-Limitierung wird auf Grundlage unseres berechtigten Interesses
        (Art. 6 Abs. 1 lit. f DSGVO) verarbeitet, um den Dienst vor missbräuchlicher Nutzung zu
        schützen.
      </P>

      {/* ── 5. Speicherdauer ───────────────────────────────────────────────────────────── */}
      <H2>5. Speicherdauer</H2>
      <P>
        Antworten und alle daraus abgeleiteten Daten (Cluster, Insights,
        Moderations-Protokolle) werden automatisch gelöscht, sobald ein Event
        länger als <strong>12 Monate</strong> ohne neue Aktivität ist. Ein
        monatlicher automatisierter Prozess führt diese Löschung durch; die
        Event-Hülle (Titel und Fragen) bleibt ohne personenbezogene Daten
        erhalten. Fingerprint-Hashes dienen ausschließlich der Missbrauchs­vermeidung
        und werden gelöscht, sobald das zugehörige Event gelöscht wird.
      </P>

      {/* ── 6. Verantwortlicher ────────────────────────────────────────────────────────── */}
      <H2>6. Verantwortlicher</H2>
      <P>
        Verantwortlich für die Datenverarbeitung im Sinne des Art. 4 Nr. 7 DSGVO ist:
      </P>
      <Address>
{`net positive ventures GmbH
c/o SleevesUp!
Bödekerstraße 1
30161 Hannover`}
      </Address>
      <P>
        E-Mail:{" "}
        {/* TODO(legal-review): legal@readycal.de is a placeholder — set up this mailbox before launch. */}
        <a href="mailto:legal@readycal.de" className="text-rc-berry underline underline-offset-2 decoration-rc-berry/40 hover:decoration-rc-berry">
          legal@readycal.de
        </a>
      </P>

      {/* ── 7. Hosting (Hetzner) ───────────────────────────────────────────────────────── */}
      <H2>7. Hosting</H2>
      <P>
        Die Plattform wird auf Servern der Hetzner Online GmbH (Industriestraße 25, 91710
        Gunzenhausen, Deutschland) betrieben. Mit Hetzner besteht ein Vertrag zur
        Auftragsverarbeitung gemäß Art. 28 DSGVO. Beim Aufruf der Webseite werden technische Daten
        (z. B. IP-Adresse, Browsertyp, Uhrzeit) verarbeitet, soweit dies für die Bereitstellung
        erforderlich ist (Art. 6 Abs. 1 lit. f DSGVO). Diese Daten werden nach Ablauf von 14 Tagen
        gelöscht oder anonymisiert.
      </P>

      {/* ── 8. Authentifizierung und Datenbank (Supabase) ─────────────────────────────── */}
      <H2>8. Authentifizierung und Datenbank (Supabase)</H2>
      <P>
        Für die Facilitator-Authentifizierung und die Speicherung von Events, Fragen und Antworten
        setzen wir Supabase ein (Supabase, Inc., 970 Toa Payoh North, #07-04, Singapore 318992).
        Der für REASONZ verwendete Projekt-Server steht in der Region „West EU (Irland)". Übertragen
        werden ausschließlich die zur Funktion notwendigen Daten: E-Mail-Adresse des Facilitators,
        Event-Inhalte, Antworttexte. Rechtsgrundlage ist die Erforderlichkeit zur Vertragserfüllung
        (Art. 6 Abs. 1 lit. b DSGVO). Datenschutzerklärung von Supabase:{" "}
        <a
          href="https://supabase.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-rc-berry underline underline-offset-2 decoration-rc-berry/40 hover:decoration-rc-berry"
        >
          supabase.com/privacy
        </a>
        .
      </P>

      {/* ── 9. KI-gestützte Auswertung (OpenAI) ──────────────────────────────────────── */}
      {/*
        TODO(legal-review): SENSITIVER PUNKT — OpenAI ist in den USA ansässig. Prüfen ob:
        1. Standard Contractual Clauses (SCCs) vorliegen und ausreichend sind.
        2. Ein Data Processing Agreement mit OpenAI abgeschlossen wurde.
        3. Teilnehmende in der Einwilligungserklärung explizit auf die USA-Übermittlung hingewiesen werden.
      */}
      <H2>9. KI-gestützte Auswertung (OpenAI) — Internationale Datenübermittlung</H2>
      <P>
        Für die Live-Clusterung und die Generierung der Beobachtungen werden die anonymen Antworten
        der Teilnehmenden zusammen mit dem Kontext des Events an{" "}
        <strong>OpenAI, L.L.C.</strong> (3180 18th Street, San Francisco, CA 94110, USA) übermittelt
        und dort durch das Modell GPT-4o verarbeitet. OpenAI verarbeitet die Daten in unserem
        Auftrag; API-Anfragen werden gemäß den vertraglichen Vorgaben nicht zum Training der Modelle
        verwendet.
      </P>
      <P>
        <strong>Internationale Datenübermittlung:</strong> Da OpenAI in den USA ansässig ist, findet
        eine Datenübermittlung in ein Drittland statt. Die Übermittlung erfolgt auf Grundlage der
        EU-Standardvertragsklauseln (SCC) gemäß Art. 46 Abs. 2 lit. c DSGVO.
        [TODO(legal-review): Bestätigen Sie, dass ein DPA mit OpenAI abgeschlossen ist und SCCs
        vorliegen.]
      </P>
      <P>
        Übertragen werden ausschließlich die Antworttexte und der vom Facilitator hinterlegte Kontext
        — keine personenbezogenen Identifikatoren. Rechtsgrundlage ist Art. 6 Abs. 1 lit. a DSGVO
        (Einwilligung durch Absenden der Antwort).
      </P>

      {/* ── 10. Webanalyse und Fehlerprotokollierung ──────────────────────────────────── */}
      <H2>10. Webanalyse und Fehlerprotokollierung</H2>
      <P>
        Wir erheben aggregierte Nutzungsstatistiken (z. B. Seitenaufrufe, Verweildauer) über ein
        selbst gehostetes Analyse-Werkzeug (Umami) auf unseren eigenen Servern. Dabei werden keine
        Cookies gesetzt, keine IP-Adressen gespeichert und keine Daten an Dritte übermittelt.
      </P>
      <P>
        Zur Stabilität von REASONZ erfassen wir technische Fehlermeldungen über ein selbst
        gehostetes Werkzeug (GlitchTip) auf unseren eigenen Servern. Erfasst werden ausschließlich
        Fehlertext, technischer Stack-Trace und Browser-/Laufzeitumgebung. IP-Adressen,
        E-Mail-Adressen, Cookies und Antworttexte werden nicht übertragen. Rechtsgrundlage ist
        unser berechtigtes Interesse an einem funktionierenden Dienst (Art. 6 Abs. 1 lit. f
        DSGVO). Die Daten werden nach 30 Tagen automatisch gelöscht.
      </P>

      {/* ── 11. Schriftarten ──────────────────────────────────────────────────────────── */}
      <H2>11. Schriftarten</H2>
      <P>
        Die verwendete Schriftart „IBM Plex Sans" wird lokal aus unserem eigenen Webspace
        ausgeliefert. Es findet keine Verbindung zu Google Fonts oder einem anderen externen
        Anbieter statt.
      </P>

      {/* ── 12. Cookies ───────────────────────────────────────────────────────────────── */}
      <H2>12. Cookies</H2>
      <P>
        REASONZ setzt ausschließlich technisch notwendige Cookies, die für die Authentifizierung
        der Facilitator-Sessions erforderlich sind (verwaltet durch Supabase). Tracking- oder
        Marketing-Cookies kommen nicht zum Einsatz. Rechtsgrundlage ist § 25 Abs. 2 Nr. 2 TTDSG.
      </P>

      {/* ── 13. Löschung / Recht auf Erasure ────────────────────────────────────────── */}
      <H2>13. Recht auf Löschung</H2>
      <P>
        Da Antworten anonym erfasst werden, ist eine gezielte Löschung einzelner Beiträge ohne
        zusätzliche Identifikation technisch nicht möglich. Facilitatoren können Events und alle
        dazugehörigen Daten jederzeit über den Facilitator-Bereich löschen. Anfragen zur Löschung
        richten Sie bitte an{" "}
        {/* TODO(legal-review): Set up legal@readycal.de before public launch. */}
        <a href="mailto:legal@readycal.de" className="text-rc-berry underline underline-offset-2 decoration-rc-berry/40 hover:decoration-rc-berry">
          legal@readycal.de
        </a>
        .
      </P>

      {/* ── 14. Ihre weiteren Rechte ─────────────────────────────────────────────────── */}
      <H2>14. Ihre weiteren Rechte</H2>
      <P>
        Sie haben jederzeit das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16),
        Löschung (Art. 17), Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit
        (Art. 20) und Widerspruch gegen die Verarbeitung (Art. 21). Wenden Sie sich dafür an{" "}
        <a href="mailto:legal@readycal.de" className="text-rc-berry underline underline-offset-2 decoration-rc-berry/40 hover:decoration-rc-berry">
          legal@readycal.de
        </a>
        .
      </P>

      {/* ── 15. Beschwerderecht ───────────────────────────────────────────────────────── */}
      <H2>15. Beschwerderecht</H2>
      <P>
        Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren. Zuständig
        ist die Landesbeauftragte für den Datenschutz Niedersachsen:{" "}
        <a
          href="https://lfd.niedersachsen.de"
          target="_blank"
          rel="noopener noreferrer"
          className="text-rc-berry underline underline-offset-2 decoration-rc-berry/40 hover:decoration-rc-berry"
        >
          lfd.niedersachsen.de
        </a>
        .
      </P>

      {/* ── 16. Kontakt Datenschutz ───────────────────────────────────────────────────── */}
      <H2>16. Kontakt</H2>
      <P>
        Bei Fragen zum Datenschutz wenden Sie sich bitte an:{" "}
        <a href="mailto:legal@readycal.de" className="text-rc-berry underline underline-offset-2 decoration-rc-berry/40 hover:decoration-rc-berry">
          legal@readycal.de
        </a>
      </P>

      {/* ── 17. Stand ─────────────────────────────────────────────────────────────────── */}
      <H2>17. Stand der Datenschutzerklärung</H2>
      <P>
        Diese Datenschutzerklärung wurde zuletzt am 24. Mai 2026 aktualisiert.
      </P>
    </>
  );
}
